/**
 * 🗒️ **자유 대화 기록 — 30일만 남고 지워진다** (FR-NPC-05 · 2026-10-14 대표님 결정).
 *
 * 어떤 질문이 들어오는지 보고 **승인 문답을 키우려고** 남긴다. 그래서:
 *  - 남기는 것은 **이미 AI 에게 보낸 값**뿐이다 — 마스킹·길이 절단을 거친 질문(`buildDocentRequest().userText`)과
 *    걸러 낸 답(`sanitizeOutbound`). 원문은 어디에도 안 남는다.
 *  - 누가 물었는지 알 수 있는 값(기기·코드·IP)은 **없다** — 날짜·친구·AI 이름뿐(불변식 1).
 *  - **30일**이 지나면 지운다. 영구히 남는 것은 운영자가 다듬어 승인한 **버튼 문답**(아이가 쓴 글이 아니다)과 횟수뿐.
 * 판정은 여기 한 곳 — 엣지(저장·지우기)·관리자(보기·초안)·웹(안내 문구)·방침이 같은 숫자를 쓴다.
 */
import type { NpcQa } from "./npcAgent";

/** 며칠 남기나 — 바꾸면 방침(`privacyText.ts`)·입력 칸 안내가 같이 바뀐다 */
export const NPC_CHAT_LOG_DAYS = 30;

/** 한 줄의 길이 상한 — 저장 전에 한 번 더 자른다(질문은 이미 잘려 오지만 게이트를 한 곳에만 두지 않는다) */
export const NPC_CHAT_LOG_MAX = { q: 300, a: 600 } as const;

export interface NpcChatLogRow {
  id: number;
  /** ISO 시각 */
  at: string;
  npc: string;
  provider: string;
  /** 마스킹된 질문 */
  q: string;
  /** 걸러 낸 답 */
  a: string;
}

/** 이 시각보다 앞선 줄은 지운다 */
export function chatLogCutoff(nowMs: number, days: number = NPC_CHAT_LOG_DAYS): string {
  return new Date(nowMs - days * 24 * 60 * 60 * 1000).toISOString();
}

/** 지울 날까지 며칠 남았나(관리자 화면에 보여 준다) — 지났으면 0 */
export function chatLogDaysLeft(at: string, nowMs: number, days: number = NPC_CHAT_LOG_DAYS): number {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.ceil((t + days * 86_400_000 - nowMs) / 86_400_000));
}

/** 서버 응답 정리(외부 입력 · 불변식 7) — 모양이 틀린 줄은 버린다 */
export function sanitizeChatLogRows(raw: unknown): NpcChatLogRow[] {
  const rows = Array.isArray((raw as { rows?: unknown } | null)?.rows) ? (raw as { rows: unknown[] }).rows : [];
  const out: NpcChatLogRow[] = [];
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    if (typeof o.id !== "number" || typeof o.at !== "string" || typeof o.npc !== "string" || typeof o.q !== "string") continue;
    out.push({
      id: o.id,
      at: o.at,
      npc: o.npc,
      provider: typeof o.provider === "string" ? o.provider : "",
      q: o.q.slice(0, NPC_CHAT_LOG_MAX.q),
      a: typeof o.a === "string" ? o.a.slice(0, NPC_CHAT_LOG_MAX.a) : "",
    });
  }
  return out;
}

/**
 * 📋 **[승인 문답 초안으로]** — 들어온 질문 한 줄을 그 친구의 문답 **대기함**에 넣을 모양으로.
 * 답은 AI 가 했던 답을 출발점으로 두되 **대기(pending)** — 운영자가 읽고 고쳐 ✅ 해야 아이에게 나간다.
 * 이미 같은 질문이 있으면 넣지 않는다(null).
 */
export function qaDraftFromLog(row: NpcChatLogRow, existing: readonly NpcQa[], nowMs: number): NpcQa | null {
  const q = row.q.trim();
  if (!q) return null;
  const norm = (s: string) => s.replace(/\s+/g, "").replace(/[?？!.。~]+$/u, "");
  if (existing.some((e) => norm(e.q) === norm(q))) return null;
  return { id: `log_${row.id}_${nowMs.toString(36)}`, q, a: row.a.trim(), status: "pending", source: "ai" };
}

/** 자주 나온 질문 — 띄어쓰기·물음표만 다른 것은 하나로 센다. 많은 순 */
export function frequentQuestions(rows: readonly NpcChatLogRow[], top = 10): { q: string; n: number }[] {
  const norm = (s: string) => s.replace(/\s+/g, "").replace(/[?？!.。~]+$/u, "");
  const map = new Map<string, { q: string; n: number }>();
  for (const r of rows) {
    const k = norm(r.q);
    if (!k) continue;
    const cur = map.get(k);
    if (cur) cur.n++;
    else map.set(k, { q: r.q.trim(), n: 1 });
  }
  return [...map.values()].sort((a, b) => b.n - a.n).slice(0, top);
}

